import { samePeriodLastYear } from '../../../utils/comparisonPeriod'
import type { Granularity } from './api'

/**
 * The comparison range for the Oversikt period graph and top lists (#928).
 * The single place these rules live; the tooltips on the comparison buttons
 * describe the same rules (oversikt.graf.modeTooltip.*).
 *
 * "i fjor" (samme periode i fjor):
 * - Day granularity: 364 days back, so a Saturday is compared with a Saturday.
 *   Bruktbil sales drop to a third on weekends.
 * - Week and month: Perioder's samePeriodLastYear. The same dates a year back,
 *   one ISO week maps to the same week number, and a month-end stays a month-end.
 *
 * "forrige periode":
 * - Day granularity: as many days back as the range is long, rounded up to
 *   whole weeks, so weekdays line up.
 * - Week and month, range starting on the 1st:
 *   - ending on a month-end: as many whole calendar months just before
 *     (September → August, Q3 → Q2);
 *   - ending inside its own month (month to date): the previous month from the
 *     1st to the same day, capped at its end (1–19 Oct → 1–19 Sep).
 * - Anything else: the equally long range just before (10–19 Oct → 30 Sep–9 Oct).
 *
 * Dates are YYYY-MM-DD and all arithmetic is in UTC.
 */
export type ComparisonMode = 'i_fjor' | 'forrige_periode'

const DAY_MS = 86_400_000
const parse = (iso: string) => new Date(`${iso}T00:00:00Z`)
const fmt = (d: Date) => d.toISOString().slice(0, 10)
const shift = (iso: string, days: number) => fmt(new Date(parse(iso).getTime() + days * DAY_MS))
const lastDay = (year: number, month: number) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

function previousCalendarRange(from: string, to: string): { from: string; to: string } | null {
  const s = parse(from)
  const e = parse(to)
  if (s.getUTCDate() !== 1) return null
  const isMonthEnd = e.getUTCDate() === lastDay(e.getUTCFullYear(), e.getUTCMonth())
  if (isMonthEnd) {
    const months = (e.getUTCFullYear() - s.getUTCFullYear()) * 12 + e.getUTCMonth() - s.getUTCMonth() + 1
    return { from: fmt(new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() - months, 1))), to: shift(from, -1) }
  }
  if (s.getUTCFullYear() === e.getUTCFullYear() && s.getUTCMonth() === e.getUTCMonth()) {
    const prev = new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() - 1, 1))
    const day = Math.min(e.getUTCDate(), lastDay(prev.getUTCFullYear(), prev.getUTCMonth()))
    return { from: fmt(prev), to: fmt(new Date(Date.UTC(prev.getUTCFullYear(), prev.getUTCMonth(), day))) }
  }
  return null
}

export function comparisonRange(
  from: string,
  to: string,
  mode: ComparisonMode,
  granularity: Granularity,
): { from: string; to: string } {
  const days = Math.round((parse(to).getTime() - parse(from).getTime()) / DAY_MS) + 1
  if (mode === 'i_fjor') {
    if (granularity === 'day') return { from: shift(from, -364), to: shift(to, -364) }
    const r = samePeriodLastYear(from, to)
    return { from: r.start, to: r.end }
  }
  if (granularity !== 'day') {
    const calendar = previousCalendarRange(from, to)
    if (calendar) return calendar
  }
  const back = granularity === 'day' ? Math.ceil(days / 7) * 7 : days
  return { from: shift(from, -back), to: shift(to, -back) }
}
